import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  providerAccountsApi,
  type PoolProvider,
  type ProviderAccount,
} from "@/api/provider-accounts";
import { modelsApi } from "@/api/models";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Plus,
  Power,
  RefreshCw,
  Shield,
  Trash2,
} from "lucide-react";

/**
 * Pooled OAuth accounts for ONE provider — rendered inside that provider's
 * card so there is a single OAuth surface per provider (replaces the old
 * primary/backup slot rows and their promote flow).
 *
 * Every enabled account is live: the credential broker load-balances agents
 * across the pool (sticky per agent, so provider prompt caches stay warm).
 * Accounts that hit usage limits cool down automatically and their agents
 * re-pin to the rest. Legacy primary/backup slots are auto-imported
 * server-side on first load.
 */
export function PooledAccountsSection({
  tenantId,
  provider,
}: {
  tenantId: string;
  provider: PoolProvider;
}) {
  const queryClient = useQueryClient();
  const [modelsOpen, setModelsOpen] = useState<string | null>(null);
  const { data: availability } = useQuery({
    queryKey: ["models", "availability", tenantId],
    queryFn: () => modelsApi.getAvailability(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
  // Models this provider's subscriptions can serve, by upstream name.
  const providerModels = [
    ...new Set(
      (availability?.models ?? [])
        .filter((m) =>
          provider === "anthropic" ? m.provider === "anthropic" : m.provider === "openai" || m.provider === "openai-codex",
        )
        .map((m) => (m.modelId.includes("/") ? m.modelId.slice(m.modelId.lastIndexOf("/") + 1) : m.modelId)),
    ),
  ].sort();
  const queryKey = ["provider-accounts", tenantId];
  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => providerAccountsApi.list(tenantId),
  });

  const [connect, setConnect] = useState<{ accountId: string; authUrl: string } | null>(null);
  const [callbackUrl, setCallbackUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const startMutation = useMutation({
    mutationFn: () => providerAccountsApi.start(tenantId, provider),
    onSuccess: (res) => {
      setConnect({ accountId: res.accountId, authUrl: res.authUrl });
      setCallbackUrl("");
      invalidate();
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => providerAccountsApi.complete(tenantId, connect!.accountId, callbackUrl),
    onSuccess: () => {
      setConnect(null);
      setCallbackUrl("");
      invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: Parameters<typeof providerAccountsApi.update>[2] }) =>
      providerAccountsApi.update(tenantId, vars.id, vars.data),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => providerAccountsApi.remove(tenantId, id),
    onSuccess: invalidate,
  });

  const rows = (data?.accounts ?? []).filter((a) => a.provider === provider && a.status !== "pending");
  const oauthName = provider === "anthropic" ? "Claude" : "ChatGPT";

  function statusChip(a: ProviderAccount) {
    if (a.status === "disabled")
      return <span className="rounded bg-surface px-1.5 py-0.5 text-[11px] text-text-muted">Disabled</span>;
    if (a.cooling)
      return (
        <span
          className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] text-amber-600"
          title={a.lastError ?? undefined}
        >
          Cooling until {a.cooldownUntil ? new Date(a.cooldownUntil * 1000).toLocaleTimeString() : "?"}
        </span>
      );
    if (a.lastError)
      return (
        <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] text-red-600" title={a.lastError}>
          Error
        </span>
      );
    return <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[11px] text-emerald-600">Active</span>;
  }

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-text-secondary">
          <Shield size={12} className="inline mr-1" />
          {oauthName} Subscription (OAuth)
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={startMutation.isPending || !!connect}
          onClick={() => startMutation.mutate()}
        >
          <Plus size={12} className="mr-1" />
          {rows.length === 0 ? "Connect" : "Add account"}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-3">
          <Loader2 size={16} className="animate-spin text-text-muted" />
        </div>
      ) : rows.length === 0 && !connect ? (
        <p className="text-[10px] text-text-muted">
          Uses {oauthName} subscription usage — no API key charges. Connect one or more accounts; agents are
          load-balanced across all of them.
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((a) => (
            <div key={a.id} className="flex items-center gap-2 rounded-md bg-surface-hover/50 px-2 py-1.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-xs text-text-primary">
                    {a.label || a.accountId || a.tokenPreview || a.id.slice(0, 8)}
                  </span>
                  {statusChip(a)}
                </div>
                <p className="text-[10px] text-text-muted">
                  {a.tokenPreview}
                  {a.lastUsedAt ? ` · last used ${new Date(a.lastUsedAt).toLocaleString()}` : " · never used"}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]">
                  <button
                    type="button"
                    className={
                      a.priority === 2
                        ? "rounded bg-sky-500/15 px-1.5 py-0.5 text-sky-600"
                        : "rounded bg-surface px-1.5 py-0.5 text-text-secondary"
                    }
                    title={
                      a.priority === 2
                        ? "Overflow: used only while every primary account is cooling. Click to make primary."
                        : "Primary: shares traffic with the other primary accounts. Click to make overflow."
                    }
                    onClick={() =>
                      updateMutation.mutate({ id: a.id, data: { priority: a.priority === 2 ? null : 2 } })
                    }
                  >
                    {a.priority === 2 ? "Overflow" : "Primary"}
                  </button>
                  <button
                    type="button"
                    className="rounded bg-surface px-1.5 py-0.5 text-text-secondary"
                    title="Limit which models this account may serve"
                    onClick={() => setModelsOpen(modelsOpen === a.id ? null : a.id)}
                  >
                    {a.allowedModels ? `Only: ${a.allowedModels.join(", ")}` : "All models"}
                  </button>
                </div>
                {modelsOpen === a.id && (
                  <div className="mt-1.5 space-y-1 rounded border border-border p-2">
                    <p className="text-[10px] text-text-muted">
                      Tick the models this account may serve. None ticked means every model.
                    </p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                      {providerModels.map((name) => {
                        const ticked = a.allowedModels?.includes(name) ?? false;
                        return (
                          <label key={name} className="flex items-center gap-1.5 text-[11px] text-text-primary">
                            <input
                              type="checkbox"
                              checked={ticked}
                              onChange={() => {
                                const current = a.allowedModels ?? [];
                                const next = ticked ? current.filter((m) => m !== name) : [...current, name];
                                updateMutation.mutate({
                                  id: a.id,
                                  data: { allowedModels: next.length > 0 ? next : null },
                                });
                              }}
                            />
                            <span className="font-mono">{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {a.cooling && (
                <Button
                  size="sm"
                  variant="ghost"
                  title="Clear cooldown"
                  onClick={() => updateMutation.mutate({ id: a.id, data: { clearCooldown: true } })}
                >
                  <RefreshCw size={12} />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                title={a.status === "disabled" ? "Enable" : "Disable"}
                onClick={() =>
                  updateMutation.mutate({
                    id: a.id,
                    data: { status: a.status === "disabled" ? "active" : "disabled" },
                  })
                }
              >
                <Power size={12} className={a.status === "disabled" ? "text-text-muted" : "text-emerald-600"} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                title="Remove account"
                onClick={() => {
                  if (window.confirm(`Remove this ${oauthName} account from the pool?`)) removeMutation.mutate(a.id);
                }}
              >
                <Trash2 size={12} className="text-red-500" />
              </Button>
            </div>
          ))}
          {rows.length > 1 && (
            <p className="text-[10px] text-text-muted">
              All enabled primary accounts are live: each agent has a home account and moves to the next healthy
              one when it cools. Overflow accounts take traffic only while every primary account is cooling.
            </p>
          )}
        </div>
      )}

      {connect && (
        <div className="space-y-2 rounded-md border border-border bg-surface/50 p-2.5">
          <p className="text-xs font-medium text-text-primary">Complete {oauthName} sign-in</p>
          <div className="flex items-center gap-2">
            <Input value={connect.authUrl} readOnly className="flex-1 text-xs font-mono" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(connect.authUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </Button>
            <a href={connect.authUrl} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline">
                <ExternalLink size={12} className="mr-1" />
                Open
              </Button>
            </a>
          </div>
          <p className="text-[10px] text-text-muted">
            Sign in with the account you want to ADD (check the browser profile!), then paste the callback URL:
          </p>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Paste callback URL here..."
              value={callbackUrl}
              onChange={(e) => setCallbackUrl(e.target.value)}
              className="flex-1 text-xs"
            />
            <Button
              size="sm"
              disabled={!callbackUrl.trim() || completeMutation.isPending}
              onClick={() => completeMutation.mutate()}
            >
              {completeMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : "Complete"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                // Abandon: remove the pending row so it doesn't linger
                removeMutation.mutate(connect.accountId);
                setConnect(null);
              }}
            >
              Cancel
            </Button>
          </div>
          {completeMutation.isError && (
            <p className="text-xs text-red-500">{(completeMutation.error as Error)?.message}</p>
          )}
        </div>
      )}
    </div>
  );
}
